import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal, Stack, TextInput, ScrollArea, Group, Text, Button, Checkbox, Badge, Box, SegmentedControl,
  Center, Loader, CloseButton, Divider,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import fuzzysort from 'fuzzysort';
import type { ListlistDto } from '@lib2/model/listlist/ListlistDto';
import type { ChildListlistOrListId } from '@lib2/model/listlist/ListlistReq';
import { useVideos } from '@/features/media/useVideos';
import { useStreams } from '@/features/streams/useStreams';
import { useStreamLiveStatus } from '@/features/streams/useStreamLiveStatus';
import { useSharedLinkConfig } from '@/features/link/useLinkConfig';
import {
  AppContentUtils,
  CHILD_KIND_COLORS,
  CHILD_KIND_LABELS,
  type ChildKind,
  type PickCandidate,
} from '../AppContentUtils';
import { ChildThumbnail } from './ChildThumbnail';

export type AddPosition = 'top' | 'end';

interface AddToCategoryDialogProps {
  opened: boolean;
  onClose: () => void;
  /** Picks in pick order + where they go; resolves when the adds have completed. */
  onAdd: (picks: ChildListlistOrListId[], kinds: ChildKind[], position: AddPosition) => Promise<void>;
  /** Series the account has (the picker excludes the ones already in the category). */
  availableSeries: ListlistDto[];
  /** llId → season count, for the "Series · N seasons" line. */
  seasonCountByLlId: Map<number, number>;
  /** Children already in the category — never offered again. */
  placedKeys: Set<string>;
  isPending?: boolean;
}

const MAX_RESULTS = 50;

const SOURCE_OPTIONS: { value: ChildKind; label: string }[] = [
  { value: 'series', label: 'Series' },
  { value: 'video', label: 'Videos' },
  { value: 'live', label: 'Live streams' },
];

const POSITION_OPTIONS: { value: AddPosition; label: string }[] = [
  { value: 'end', label: 'End' },
  { value: 'top', label: 'Top' },
];

/**
 * ONE picker for everything a category can hold (design ADR-0014): series,
 * videos and live streams, chosen across sources into one tray, placed in
 * pick order at the top or the end of the row. Only the active source's
 * library query runs (a 30k-video account never pays for videos while picking
 * streams), fuzzysort targets are prepared once per source, at most 50 rows
 * ever render.
 */
export function AddToCategoryDialog({
  opened,
  onClose,
  onAdd,
  availableSeries,
  seasonCountByLlId,
  placedKeys,
  isPending,
}: AddToCategoryDialogProps) {
  const [source, setSource] = useState<ChildKind>('series');
  const [searchText, setSearchText] = useState('');
  const [position, setPosition] = useState<AddPosition>('end');
  // Insertion-ordered: the order picked is the order placed.
  const [picks, setPicks] = useState<Map<string, PickCandidate>>(new Map());
  const searchRef = useRef<HTMLInputElement>(null);

  const { data: linkConfig } = useSharedLinkConfig();
  const { videos, isLoading: videosLoading } = useVideos({ enabled: opened && source === 'video', refetchOnMount: false });
  const { streams, isLoading: streamsLoading } = useStreams({ enabled: opened && source === 'live', refetchOnMount: false });
  const { liveStatusMap } = useStreamLiveStatus({ enabled: opened && source === 'live' });
  const isLoading = source === 'video' ? videosLoading : source === 'live' ? streamsLoading : false;

  // Focus the search box whenever the source changes (the fastest path is type → space → add).
  useEffect(() => {
    if (opened) searchRef.current?.focus();
  }, [opened, source]);

  const candidates = useMemo<PickCandidate[]>(() => {
    const cfg = linkConfig ?? null;
    let all: PickCandidate[];
    if (source === 'series') {
      all = availableSeries.map((s) => AppContentUtils.seriesCandidate(s, { linkConfig: cfg, seasonCountByLlId }));
    } else if (source === 'video') {
      all = videos.map((v) => AppContentUtils.videoCandidate(v, cfg));
    } else {
      all = streams.map((st) => AppContentUtils.streamCandidate(st, cfg, liveStatusMap));
    }
    return all.filter((c) => !placedKeys.has(c.key));
  }, [source, availableSeries, seasonCountByLlId, videos, streams, linkConfig, liveStatusMap, placedKeys]);

  // fuzzysort targets, prepared once per source/candidate set (not per keystroke).
  const prepared = useMemo(
    () => candidates.map((c) => ({ candidate: c, prepared: fuzzysort.prepare(c.searchTitle) })),
    [candidates],
  );

  const { results, totalMatching } = useMemo(() => {
    if (!searchText.trim()) {
      return { results: candidates.slice(0, MAX_RESULTS), totalMatching: candidates.length };
    }
    const matches = fuzzysort.go(searchText, prepared, { key: 'prepared', limit: MAX_RESULTS + 100 });
    return {
      results: matches.slice(0, MAX_RESULTS).map((m) => m.obj.candidate),
      totalMatching: matches.length,
    };
  }, [searchText, candidates, prepared]);

  const togglePick = useCallback((candidate: PickCandidate) => {
    setPicks((prev) => {
      const next = new Map(prev);
      if (next.has(candidate.key)) next.delete(candidate.key);
      else next.set(candidate.key, candidate);
      return next;
    });
  }, []);

  const removePick = useCallback((key: string) => {
    setPicks((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const pickList = useMemo(() => Array.from(picks.values()), [picks]);
  const pickKinds = useMemo(() => pickList.map((p) => p.kind), [pickList]);
  const ctaLabel = AppContentUtils.pickCtaLabel(pickKinds);

  const reset = useCallback(() => {
    setSearchText('');
    setPicks(new Map());
    setPosition('end');
    setSource('series');
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleAdd = useCallback(async () => {
    if (pickList.length === 0) return;
    await onAdd(pickList.map((p) => p.ref), pickKinds, position);
    reset();
  }, [pickList, pickKinds, position, onAdd, reset]);

  const sourceNoun = source === 'series' ? 'series' : source === 'video' ? 'videos' : 'live streams';
  const emptyCopy = candidates.length === 0
    ? source === 'series'
      ? 'Every series is already in this category, or none exist yet.'
      : `Every ${sourceNoun} in your library is already in this category, or none exist yet.`
    : `No ${sourceNoun} match "${searchText}".`;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add to category"
      size="xl"
      styles={{ body: { paddingTop: 0 } }}
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Series, videos and live streams sit in one row on Home, in the order you set. A series
          tile opens the series; a video or stream tile plays.
        </Text>

        <Group justify="space-between" wrap="nowrap" gap="sm">
          <SegmentedControl
            size="sm"
            value={source}
            onChange={(value) => setSource(value as ChildKind)}
            data={SOURCE_OPTIONS}
          />
          <TextInput
            ref={searchRef}
            placeholder={`Search ${sourceNoun}…`}
            leftSection={<IconSearch size={16} />}
            value={searchText}
            onChange={(e) => setSearchText(e.currentTarget.value)}
            style={{ flex: 1 }}
            data-autofocus
          />
        </Group>

        <Text size="xs" c="dimmed" mih={18}>
          {totalMatching > MAX_RESULTS
            ? `Showing the first ${MAX_RESULTS} of ${totalMatching}. Type to narrow it down.`
            : totalMatching > 0
              ? `${totalMatching} ${totalMatching === 1 ? 'match' : 'matches'}`
              : ''}
        </Text>

        <ScrollArea h={380} type="auto">
          {isLoading ? (
            <Center py="xl">
              <Loader size="sm" />
            </Center>
          ) : results.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl" size="sm">
              {emptyCopy}
            </Text>
          ) : (
            <Stack gap={0}>
              {results.map((candidate) => {
                const isPicked = picks.has(candidate.key);
                return (
                  <Box
                    key={candidate.key}
                    px="sm"
                    py={6}
                    role="option"
                    aria-selected={isPicked}
                    onClick={() => togglePick(candidate)}
                    style={{
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--mantine-color-gray-2)',
                      backgroundColor: isPicked ? 'var(--mantine-primary-color-light)' : undefined,
                    }}
                  >
                    <Group gap="sm" wrap="nowrap">
                      <Checkbox
                        checked={isPicked}
                        onChange={() => togglePick(candidate)}
                        onClick={(e) => e.stopPropagation()}
                        tabIndex={-1}
                        aria-label={`Pick ${candidate.title}`}
                      />
                      <ChildThumbnail
                        url={candidate.thumbnailUrl}
                        kind={candidate.kind}
                        title={candidate.title}
                        width={80}
                        onAir={candidate.isOnAir}
                      />
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={500} truncate="end">{candidate.title}</Text>
                        <Text size="xs" c="dimmed" truncate="end">
                          {candidate.subtitle ?? (candidate.kind === 'live' ? 'Live stream' : '')}
                        </Text>
                      </Box>
                      {!candidate.isActive && (
                        <Badge size="xs" variant="light" color="gray">Inactive</Badge>
                      )}
                      <Badge size="xs" variant="light" color={CHILD_KIND_COLORS[candidate.kind]} style={{ flexShrink: 0 }}>
                        {CHILD_KIND_LABELS[candidate.kind]}
                      </Badge>
                    </Group>
                  </Box>
                );
              })}
            </Stack>
          )}
        </ScrollArea>

        <Divider />

        {/* Selection tray: every pick across sources, in the order they will be placed */}
        <Box>
          <Group justify="space-between" wrap="nowrap" mb={pickList.length ? 6 : 0}>
            <Text size="sm" fw={500}>
              {pickList.length === 0 ? 'Nothing picked yet' : `${pickList.length} picked, in this order`}
            </Text>
            <Group gap={8} wrap="nowrap">
              <Text size="sm" c="dimmed">Add to:</Text>
              <SegmentedControl
                size="xs"
                value={position}
                onChange={(value) => setPosition(value as AddPosition)}
                data={POSITION_OPTIONS}
              />
            </Group>
          </Group>
          {pickList.length > 0 && (
            <Group gap={6}>
              {pickList.map((p) => (
                <Badge
                  key={p.key}
                  variant="light"
                  color={CHILD_KIND_COLORS[p.kind]}
                  size="lg"
                  pr={4}
                  rightSection={
                    <CloseButton size="xs" aria-label={`Remove ${p.title} from the picks`} onClick={() => removePick(p.key)} />
                  }
                  style={{ textTransform: 'none', maxWidth: 260 }}
                >
                  <Text size="xs" span truncate="end" style={{ display: 'inline-block', maxWidth: 200, verticalAlign: 'middle' }}>
                    {p.title}
                  </Text>
                </Badge>
              ))}
            </Group>
          )}
        </Box>

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose} disabled={isPending}>Cancel</Button>
          <Button onClick={handleAdd} disabled={pickList.length === 0} loading={isPending}>
            {ctaLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
